<!-- Every group at a glance, with People's totals, and a way into each group. -->
<script lang="ts">
  import {
    collectedByMonth,
    computeLedger,
    firstActivity,
    formatMicro,
    owedToYouMicro,
    purchaseSpan,
    receivedSinceMicro,
    trailingMonths,
    youOweMicro,
    owedTrend,
    trendDates,
    rangeBreakdowns,
    tutoringCard,
    type DateRange,
    type GroupState,
    type Ledger,
  } from "@split/core";
  import RangePicker from "../components/RangePicker.svelte";
  import LineChart from "../components/charts/LineChart.svelte";
  import ColumnChart from "../components/charts/ColumnChart.svelte";
  import BarPanels, { type Section } from "../components/BarPanels.svelte";
  import { monthLabel } from "../lib/format.ts";
  import { app } from "../lib/store.svelte.ts";
  import { groupPath, link } from "../lib/router.svelte.ts";
  import { shortDate } from "../lib/format.ts";
  import JoinRequests from "../components/JoinRequests.svelte";
  import Yours from "./overview/Yours.svelte";
  import { nav } from "../lib/nav.svelte.ts";
  import { people, peopleRanked, peopleTotals } from "../lib/people.svelte.ts";
  import { fill, shows } from "../settings/index.ts";
  import * as A from "../settings/amounts.ts";
  import * as TT from "../settings/tutoring.ts";
  import { localNow, tutoring } from "../lib/tutoring.svelte.ts";
  import { formatCents } from "../lib/format.ts";

  // Who owes whom is main People's one total per person, every group and Tutoring netted
  // (Miles, 2026-10-23: Jianing Dong is owed $10.63 here too, not $110.63 from one group).
  $effect(() => {
    if (nav.isOwner) void people.load();
  });
  // And again whenever saved changes move on, in any group.
  $effect(() => {
    void app.confirmed;
    void app.othersVersion;
    if (nav.isOwner) people.refreshSoon();
  });
  // Tutoring shows here like a group (Miles, 2026-11-02), read fresh whenever the Overview opens.
  $effect(() => {
    if (nav.isOwner) void tutoring.load();
  });
  const tcard = $derived(
    tutoring.loaded ? tutoringCard(tutoring.students, tutoring.tutors, tutoring.entries, tutoring.slots, tutoring.claims, localNow()) : null,
  );
  const when = (dt: string) =>
    `${shortDate(dt.slice(0, 10))} ${dt.slice(11, 16)}`;
  const MICRO = 10_000; // micro-dollars in a cent
  const ranked = $derived(peopleRanked(people.rows ?? []));

  interface Card {
    id: string;
    name: string;
    ledger: Ledger | null;
    state: GroupState | null;
  }

  let cards = $state<Card[]>([]);

  $effect(() => {
    // Re-run when groups change or the open group gets new changes.
    const groups = app.groups;
    void app.current;
    void app.othersVersion; // another group got new changes
    const day = app.today;
    let cancelled = false;
    void Promise.all(
      groups.map(async (g) => {
        const s = await app.groupSnapshot(g.id);
        return {
          id: g.id,
          name: g.name,
          ledger: s ? computeLedger(s, day) : null,
          state: s,
        };
      }),
    ).then((c) => {
      if (!cancelled) cards = c;
    });
    return () => (cancelled = true);
  });

  // Sums of what each row shows (whole cents), so leftovers under half a cent never add up to a phantom $0.01.
  const totals = $derived(peopleTotals(people.rows ?? []));
  const totalOwed = $derived(totals.owedToYouCents * MICRO);
  const totalYouOwe = $derived(totals.youOweCents * MICRO);

  function lastTrip(L: Ledger): string | null {
    const dates = L.trips.map((t) => t.date).sort();
    return dates.at(-1) ?? null;
  }

  // ---- the dashboard: everything across your groups
  const live = $derived(cards.filter((c): c is Card & { ledger: Ledger; state: GroupState } => !!c.ledger && !!c.state));
  const SERIES = Array.from({ length: 8 }, (_, i) => `var(--series-${i + 1})`);

  const trend = $derived.by(() => {
    const starts = live.map((c) => firstActivity(c.state, c.ledger)).filter((d): d is string => !!d).sort();
    if (!starts.length) return [];
    let ends = trendDates(starts[0]!, app.today);
    if (ends.length > 104) ends = ends.slice(-104);
    const sums = ends.map(() => 0);
    for (const c of live) owedTrend(c.state, c.ledger, ends).forEach((p, i) => (sums[i]! += p.valueMicro));
    // The last point is today: show exactly what the tiles show (no leftover fractions of a cent).
    sums[sums.length - 1] = totalOwed - totalYouOwe;
    return ends.map((d, i) => ({ label: shortDate(d), value: sums[i]! }));
  });

  const months = $derived(trailingMonths(app.today));
  const monthLabels = $derived(months.map((m) => monthLabel(m).slice(0, 3)));

  const spendSeries = $derived(
    live.slice(0, 8).map((c, k) => {
      const by = new Map(c.ledger.insights.byMonth.map((b) => [b.label, b.totalMicro]));
      return { name: c.name, color: SERIES[k]!, values: months.map((m) => by.get(m) ?? 0) };
    }),
  );

  const collected = $derived.by(() => {
    const sums = months.map(() => 0);
    for (const c of live) {
      const by = collectedByMonth(c.state);
      months.forEach((m, i) => (sums[i]! += by.get(m) ?? 0));
    }
    return [{ name: "Came in", color: "var(--series-3)", values: sums }];
  });

  const last30 = $derived(receivedSinceMicro(live.map((c) => c.state), app.today, 30));

  const thisMonth = $derived(
    live.reduce((s, c) => s + (c.ledger.insights.byMonth.find((b) => b.label === app.today.slice(0, 7))?.totalMicro ?? 0), 0),
  );

  let storeRange = $state<DateRange>({ from: null, to: null });
  /** First and last purchase dates across your groups: the range slider's ends. */
  const dataSpan = $derived(purchaseSpan(live.map((c) => c.state)));
  const panels = $derived<Section[]>([
    {
      title: A.PANEL_OWED_TITLE,
      rows: ranked.owes.slice(0, 8).map((x) => ({ label: x.label, totalMicro: x.cents * MICRO })),
    },
    {
      title: A.PANEL_OWE_TITLE,
      rows: ranked.owed.slice(0, 8).map((x) => ({ label: x.label, totalMicro: -x.cents * MICRO })),
    },
    {
      title: "Top stores",
      rows: (() => {
        const m = new Map<string, number>();
        for (const c of live)
          for (const b of rangeBreakdowns(c.state, c.ledger, storeRange).byStore) m.set(b.label, (m.get(b.label) ?? 0) + b.totalMicro);
        return [...m].map(([label, totalMicro]) => ({ label, totalMicro })).sort((a, b) => b.totalMicro - a.totalMicro).slice(0, 8);
      })(),
    },
  ]);
</script>

{#if nav.asMember}
<div class="page"><Yours /></div>
{:else}
<div class="page">
  <div class="page-head">
    <div>
      <h1>Overview</h1>
      <p class="lede">
        {#if totalOwed === 0 && totalYouOwe === 0}
          {A.TOTALS_SQUARE}
        {:else}
          {fill(A.TOTALS_LINE, { owedLabel: A.TOTAL_OWED_LABEL, owed: formatMicro(totalOwed), oweLabel: A.TOTAL_OWE_LABEL, owe: formatMicro(totalYouOwe) })}
        {/if}
      </p>
    </div>
    <a href="/you#groups" use:link class="btn">New group</a>
  </div>

  <JoinRequests />

  {#if live.length}
    <section class="dash" aria-label="Dashboard">
      <div class="tiles">
        <div class="tile"><span class="muted">{A.TOTAL_OWED_LABEL}</span><strong class="num">{formatMicro(totalOwed)}</strong></div>
        <div class="tile"><span class="muted">{A.TOTAL_OWE_LABEL}</span><strong class="num">{formatMicro(totalYouOwe)}</strong></div>
        <div class="tile"><span class="muted">Came in, last 30 days</span><strong class="num">{formatMicro(last30)}</strong></div>
        <div class="tile"><span class="muted">Spent this month</span><strong class="num">{formatMicro(thisMonth)}</strong></div>
      </div>
      <div class="charts">
        <div class="panel wide"><LineChart title={A.CHART_OWED_TITLE} points={trend} /></div>
        <div class="panel"><ColumnChart title="Spending by month" labels={monthLabels} series={spendSeries} /></div>
        <div class="panel"><ColumnChart title="Money that came in" labels={monthLabels} series={collected} /></div>
      </div>
      <div class="range-row"><span class="muted">Top stores for</span><div class="rp"><RangePicker key="overview-stores" bind:range={storeRange} min={dataSpan.lo} max={dataSpan.hi} /></div></div>
      <BarPanels sections={panels} />
    </section>
    <h2 class="groups-h">Your groups</h2>
  {/if}

  <div class="grid">
    {#each cards as c (c.id)}
      {@const L = c.ledger}
      <a class="group" href={groupPath(c.id)} use:link class:open={c.id === app.groupId}>
        <div class="top">
          <h2>{c.name}</h2>
          {#if c.id === app.groupId}<span class="tag">Open</span>{/if}
        </div>
        {#if L}
          {@const owed = owedToYouMicro(L)}
          {@const owe = youOweMicro(L)}
          <p class="big num" class:zero={owed === 0}>{formatMicro(owed)}</p>
          {@const owers = L.people.filter((p) => p.status === "owes").length}
          <p class="muted">
            {owers ? fill(owers === 1 ? A.GROUP_CARD_PEOPLE_ONE : A.GROUP_CARD_PEOPLE_MANY, { count: owers }) : A.GROUP_CARD_SQUARE}{#if owe > 0} · {A.TOTAL_OWE_LABEL} {formatMicro(owe)}{/if}
          </p>
          <dl>
            <div><dt>People</dt><dd>{L.people.length}</dd></div>
            <div><dt>Items</dt><dd>{L.items.length}</dd></div>
            <div><dt>Spent</dt><dd class="num">{formatMicro(L.totals.allMicro)}</dd></div>
            <div><dt>Last trip</dt><dd>{lastTrip(L) ? shortDate(lastTrip(L)!) : "None yet"}</dd></div>
          </dl>
        {:else}
          <p class="muted">Downloading it to this device…</p>
        {/if}
      </a>
    {/each}
    {#if tcard && (tcard.students || tcard.tutors)}
      <a class="group" href="/tutoring/calendar" use:link aria-label={TT.OV_TUTORING_TITLE}>
        <div class="top">
          <h2>{TT.OV_TUTORING_TITLE}</h2>
          {#if tcard.requests && shows(TT.OV_TUTORING_REQUESTS)}<span class="tag">{fill(TT.OV_TUTORING_REQUESTS, { n: tcard.requests })}</span>{/if}
        </div>
        <p class="big num" class:zero={tcard.owedToYouCents === 0}>{formatCents(tcard.owedToYouCents)}</p>
        <p class="muted">
          {fill(TT.OV_TUTORING_PEOPLE, { students: tcard.students, tutors: tcard.tutors })}{#if tcard.youOweCents > 0} · {A.TOTAL_OWE_LABEL} {formatCents(tcard.youOweCents)}{/if}
        </p>
        <dl>
          <div><dt>{TT.OV_TUTORING_LESSONS}</dt><dd>{tcard.lessonsThisMonth}</dd></div>
          <div><dt>{TT.OV_TUTORING_NEXT}</dt><dd>{tcard.nextLesson ? when(tcard.nextLesson) : TT.OV_TUTORING_NONE}</dd></div>
          <div><dt>{TT.OV_TUTORING_LAST}</dt><dd>{tcard.lastLesson ? when(tcard.lastLesson) : TT.OV_TUTORING_NONE}</dd></div>
          <div><dt>{TT.CAL_CHIP_OPEN}</dt><dd>{tcard.needsTutor}</dd></div>
        </dl>
      </a>
    {/if}
  </div>
</div>
{/if}

<style>
  .dash {
    display: grid;
    gap: 16px;
    margin-bottom: 28px;
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
  }

  .tile {
    display: grid;
    gap: 4px;
    padding: 14px 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
    font-size: var(--t-sm);
  }

  .tile strong {
    font-size: var(--t-xl);
  }

  .charts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .panel {
    padding: 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
    min-width: 0;
  }

  .panel.wide {
    grid-column: 1 / -1;
  }

  @media (max-width: 720px) {
    .charts {
      grid-template-columns: 1fr;
    }
  }

  .range-row {
    display: grid;
    gap: 8px;
    margin-bottom: -6px;
  }

  .rp {
    max-width: 560px;
  }

  .groups-h {
    font-size: var(--t-lg);
    margin: 0 0 10px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
  }

  .group {
    display: grid;
    gap: 6px;
    align-content: start;
    padding: 18px;
    border-radius: var(--r-lg, 14px);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
    color: var(--ink);
    text-decoration: none;
    transition: box-shadow 0.15s, transform 0.15s;
  }

  .group:hover {
    box-shadow: inset 0 0 0 1px var(--ink-2);
  }

  .group:active {
    transform: scale(0.99);
  }

  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  h2 {
    font-size: var(--t-lg);
    margin: 0;
  }

  .tag {
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--muted);
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--wash);
  }

  .big {
    font-size: var(--t-2xl);
    font-weight: 700;
    margin: 6px 0 0;
    line-height: 1.1;
  }

  .big.zero {
    color: var(--muted);
  }

  .muted {
    margin: 0;
    font-size: var(--t-sm);
  }

  dl {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px 12px;
    margin: 10px 0 0;
    padding-top: 12px;
    border-top: 1px solid var(--rule);
  }

  dt {
    font-size: var(--t-xs);
    color: var(--muted);
  }

  dd {
    margin: 0;
    font-weight: 600;
  }

</style>
